import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {CanvasRef, canvasRefKey} from 'canvas-core/chat/bnchCanvasMarkdown';
import {ChatCanvasArtifactView} from 'canvas-core/chat/ChatCanvasArtifactView';
import {Artifact, ArtifactType} from 'llm/chat-library/ArtifactTypes';
import {ActiveCanvas} from 'llm/data-capture/DataCaptureTypes';
import * as SidepanelActions from 'nav/sidepanel/SidepanelActions';
import useIsPhoneWidth from 'ui-library/hooks/useIsPhoneWidth';

export interface CanvasArtifactData extends CanvasRef {
  chatSessionId: string;
}

export type CanvasArtifact = Artifact<CanvasArtifactData>;

function hasActiveCanvas(state: object): state is {active_canvas: ActiveCanvas} {
  return 'active_canvas' in state && (state as {active_canvas: unknown}).active_canvas != null;
}

function activeCanvasToRef(activeCanvas: ActiveCanvas): CanvasRef {
  return {
    canvasDefinitionId: activeCanvas.canvas_definition_id,
    canvasVersionId: activeCanvas.canvas_version_id,
  };
}

/**
 * The canvasDefinitionId of the application state's active canvas, or null if there is none or
 * canvases are disabled. Read straight from the state, so it is current in the same render the state
 * changes, unlike the canvas artifact, which this hook syncs from the state in an effect.
 */
export function getActiveCanvasDefinitionId(latestApplicationState: object | null): string | null {
  if (!BenchlingConfig.ENABLE_CANVAS_CORE || latestApplicationState == null) {
    return null;
  }
  return hasActiveCanvas(latestApplicationState)
    ? latestApplicationState.active_canvas.canvas_definition_id
    : null;
}

export interface UseCanvasArtifactOptions {
  chatSessionId: string;
  latestApplicationState: object | null;
  /**
   * Whether the right-sidebar layout is in use (LLM_ENABLE_RIGHT_SIDEBAR). When true, opening a canvas
   * only registers it as the active canvas; surfacing it is the sidebar's responsibility, so the legacy
   * panel-expand and phone-overlay side effects are skipped.
   */
  usesRightSidebar: boolean;
}

export interface UseCanvasArtifactResult {
  canvasArtifact: CanvasArtifact | null;
  isPhoneCanvasOpen: boolean;
  openCanvas: (canvas: CanvasRef) => void;
  closePhoneCanvas: () => void;
}

export function useCanvasArtifact({
  chatSessionId,
  latestApplicationState,
  usesRightSidebar,
}: UseCanvasArtifactOptions): UseCanvasArtifactResult {
  const isPhoneWidth = useIsPhoneWidth();
  const [activeCanvas, setActiveCanvas] = useState<CanvasRef | null>(null);
  const [isPhoneCanvasOpen, setIsPhoneCanvasOpen] = useState<boolean>(false);
  const prevLatestCanvasKeyRef = useRef<string | null>(null);
  const hasCompletedInitialSyncRef = useRef<boolean>(false);

  const latestCanvasFromState = useMemo(() => {
    if (latestApplicationState == null || !hasActiveCanvas(latestApplicationState)) {
      return null;
    }
    return activeCanvasToRef(latestApplicationState.active_canvas);
  }, [latestApplicationState]);

  const latestCanvasKey = latestCanvasFromState != null ? canvasRefKey(latestCanvasFromState) : null;

  const openCanvas = useCallback(
    (canvas: CanvasRef) => {
      setActiveCanvas(canvas);
      if (usesRightSidebar) {
        return;
      }
      if (isPhoneWidth) {
        setIsPhoneCanvasOpen(true);
      } else {
        SidepanelActions.expand();
      }
    },
    [isPhoneWidth, usesRightSidebar]
  );

  const closePhoneCanvas = useCallback(() => {
    setIsPhoneCanvasOpen(false);
  }, []);

  // Track active_canvas from application state. Auto-expand when a new canvas appears mid-session.
  useEffect(() => {
    if (!BenchlingConfig.ENABLE_CANVAS_CORE) {
      return;
    }

    if (latestCanvasKey == null || latestCanvasFromState == null) {
      if (latestApplicationState != null) {
        hasCompletedInitialSyncRef.current = true;
      }
      return;
    }

    const prevKey = prevLatestCanvasKeyRef.current;
    if (prevKey === latestCanvasKey) {
      hasCompletedInitialSyncRef.current = true;
      return;
    }

    prevLatestCanvasKeyRef.current = latestCanvasKey;
    setActiveCanvas(latestCanvasFromState);

    const shouldAutoExpand = hasCompletedInitialSyncRef.current;
    hasCompletedInitialSyncRef.current = true;

    if (!shouldAutoExpand || usesRightSidebar) {
      return;
    }

    if (isPhoneWidth) {
      setIsPhoneCanvasOpen(true);
    } else {
      SidepanelActions.expand();
    }
  }, [latestApplicationState, latestCanvasFromState, latestCanvasKey, isPhoneWidth, usesRightSidebar]);

  const canvasArtifact = useMemo((): CanvasArtifact | null => {
    if (activeCanvas == null) {
      return null;
    }
    return {
      artifactType: ArtifactType.CANVAS,
      open: () => {},
      data: {...activeCanvas, chatSessionId},
      Component: ChatCanvasArtifactView,
    };
  }, [activeCanvas, chatSessionId]);

  return {
    canvasArtifact,
    isPhoneCanvasOpen,
    openCanvas,
    closePhoneCanvas,
  };
}
